import {
  CACHE_HEADER_ENABLED,
  CACHE_HEADER_STALE_TIME,
  listenForCacheUpdate,
} from "@sinch/cache-helpers";
import { useState, useCallback, useEffect } from "react";
import { useLoginToken } from ".";

interface FetchOptions<T = unknown> {
  autoFetch?: boolean;
  withToken?: boolean;
  withCache?: boolean;
  cacheDuration?: number;
  initialData?: T;
  extraRequestOptions?: RequestInit;
}

export const useFetchData = <T>(
  url: string,
  /** make sure to use useCallback to avoid fetching in a loop */
  parser: (x: unknown) => T,
  { autoFetch = true, ...options }: FetchOptions<T>
) => {
  const [data, setData] = useState<T | null>(options.initialData ?? null);
  const [loading, setLoading] = useState(autoFetch);

  const loginToken = useLoginToken();

  const buildHeaders = useCallback(
    async (
      method: string,
      token?: string,
      withCache = true,
      cacheDuration?: number
    ) => {
      const headers: RequestInit["headers"] = {};

      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (method === "GET" && withCache) headers[CACHE_HEADER_ENABLED] = "true";
      if (method === "GET" && cacheDuration)
        headers[CACHE_HEADER_STALE_TIME] = `${cacheDuration}`;

      return headers;
    },
    []
  );

  const requestBuilder = useCallback(
    (
        url: string,
        parser: (x: unknown) => T,
        options: Omit<FetchOptions, "autoFetch"> & {
          token?: string | undefined;
        }
      ) =>
      (method: string) =>
      async (onExecuteOption?: RequestInit): Promise<T | null> => {
        if (options.withToken && !options.token) return null;
        try {
          setLoading(true);
          const headers = await buildHeaders(
            method,
            options.token,
            options.withCache,
            options.cacheDuration
          );
          const response = await fetch(url, {
            ...options.extraRequestOptions,
            ...onExecuteOption,
            method,
            headers: {
              ...headers,
              ...options.extraRequestOptions?.headers,
              ...onExecuteOption?.headers,
            },
          });

          const data = await response.json();
          const parsedData = parser(data);

          setData(parsedData);
          return parsedData;
        } catch (error) {
          console.error(`Failed to fetch data for ${method} - ${url}:`, error);
          return null;
        }
      },
    [buildHeaders]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getRequest = useCallback(
    requestBuilder(url, parser, {
      token: loginToken?.token,
      ...options,
    }),
    [loginToken?.token, requestBuilder]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getData = useCallback(getRequest("GET"), [getRequest]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const putData = useCallback(getRequest("PUT"), [getRequest]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const postData = useCallback(getRequest("POST"), [getRequest]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const deleteData = useCallback(getRequest("DELETE"), [getRequest]);

  useEffect(() => {
    if (autoFetch) {
      getData();
    }
  }, [autoFetch, getData]);

  useEffect(
    () =>
      listenForCacheUpdate(new URL(url), async (response) => {
        const data = await response.json();
        const parsedData = parser(data);

        setData(parsedData);
      }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { data, getData, putData, postData, deleteData, loading };
};
